import { FlowType } from "@redotech/redo-model/return-flow";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { TextInput } from "@redotech/redo-web/text-input";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import classnames from "classnames";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import * as flowEditorHeaderCss from "./flow-editor-header-buttons.module.css";

export interface FlowEditorHeaderProps {
  flowType: FlowType;

  showSaveAndCancel: boolean;
  cancelDisabled: boolean;
  saveDisabled: string | undefined;
  savePending: boolean;

  showPublishButton: boolean;
  publishClicked: () => void;
  showUnpublishButton: boolean;
  unpublishClicked: () => void;
  publishPending: boolean;

  rightPanelOpen: boolean;

  detailsClicked: () => void;
  cancelClicked: () => void;
  saveClicked: () => void;

  ruleName: string;
  setRuleName: (name: string) => void;

  defaultButtonClicked?: () => void; // button that is visible when all others are not
  defaultButtonPending?: boolean;
  defaultButtonText?: string;

  isNewEditor?: boolean;
  toggleEditorMode?: () => void;
  closeNavigationPath?: string;
}

export const FlowEditorHeaderButtons = memo(function FlowEditorHeaderButtons({
  flowType,
  showSaveAndCancel,
  cancelDisabled,
  saveDisabled,
  savePending,
  publishPending,
  showPublishButton,
  showUnpublishButton,
  rightPanelOpen,
  ruleName,
  setRuleName,
  detailsClicked,
  cancelClicked,
  saveClicked,
  publishClicked,
  unpublishClicked,
  defaultButtonClicked,
  defaultButtonPending,
  defaultButtonText,
  isNewEditor,
  toggleEditorMode,
  closeNavigationPath = "/settings/flows",
}: FlowEditorHeaderProps) {
  const navigate = useNavigate();
  const ruleSpecificItems = flowType === FlowType.RULE && (
    <>
      <TextInput
        onChange={setRuleName}
        placeholder="Give this rule a name"
        value={ruleName}
      />
      <RedoButton
        hierarchy="tertiary"
        onClick={detailsClicked}
        size="lg"
        text="Details"
      />
    </>
  );

  const showDefaultButton =
    defaultButtonClicked &&
    defaultButtonText &&
    !showSaveAndCancel &&
    !showPublishButton;

  return (
    <Flex
      className={classnames({
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [flowEditorHeaderCss.rightPanelOpenOld]: rightPanelOpen && !isNewEditor,
      })}
    >
      {ruleSpecificItems}
      {toggleEditorMode && (
        <RedoButton
          hierarchy="tertiary"
          onClick={toggleEditorMode}
          size="sm"
          text={isNewEditor ? "Switch to old editor" : "Switch to new editor"}
        />
      )}
      {isNewEditor && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => navigate(closeNavigationPath)}
          size="sm"
          text="Close"
        />
      )}
      {showSaveAndCancel && (
        <>
          <RedoButton
            disabled={cancelDisabled}
            hierarchy="secondary"
            onClick={cancelClicked}
            size="sm"
            text="Cancel"
          />
          <DisabledTooltip disabledMessage={saveDisabled}>
            <RedoButton
              disabled={!!saveDisabled}
              hierarchy="primary"
              onClick={saveClicked}
              pending={savePending}
              size="sm"
              text="Save"
            />
          </DisabledTooltip>
        </>
      )}
      {showUnpublishButton && (
        <RedoButton
          hierarchy="secondary"
          onClick={unpublishClicked}
          pending={publishPending}
          size="sm"
          text="Unpublish"
        />
      )}
      {showPublishButton && (
        <RedoButton
          dataTestId="publish-flow-button"
          hierarchy="primary"
          onClick={publishClicked}
          pending={publishPending}
          size="sm"
          text="Publish"
          theme="success"
        />
      )}
      {showDefaultButton && (
        <RedoButton
          hierarchy="secondary"
          onClick={defaultButtonClicked}
          pending={defaultButtonPending ?? false}
          size="sm"
          text={defaultButtonText}
        />
      )}
    </Flex>
  );
});
